import { ModuleDataSyncService } from '@application/persistence/ModuleDataSyncService';
import type { ReversementIRPPRecord } from '@shared/types/ReversementIRPP';
import { uuid } from '@shared/utils/uuid';

const NAMESPACE = 'fiscalite.reversement_irpp';
const LEGACY_KEY = 'nexxus.fiscalite.reversement_irpp';

type Payload = { items: ReversementIRPPRecord[] };

function nowIso(): string {
  return new Date().toISOString();
}

async function load(tenantId: string): Promise<ReversementIRPPRecord[]> {
  const data = await ModuleDataSyncService.getJson<Payload>(
    tenantId,
    NAMESPACE,
    { items: [] },
    LEGACY_KEY,
  );
  return Array.isArray(data.items) ? data.items : [];
}

async function save(tenantId: string, items: ReversementIRPPRecord[]): Promise<void> {
  await ModuleDataSyncService.setJson(tenantId, NAMESPACE, { items }, LEGACY_KEY);
}

export async function listReversementsIRPP(tenantId: string): Promise<ReversementIRPPRecord[]> {
  return load(tenantId);
}

export async function getReversementIRPP(
  tenantId: string,
  periode: string,
): Promise<ReversementIRPPRecord | undefined> {
  const items = await load(tenantId);
  return items.find((r) => r.periode === periode);
}

/**
 * Enregistre (ou met à jour) le reversement IRPP d’une période.
 * `date_reversement` ISO `YYYY-MM-DD` — obligatoire pour passer en statut « Reversé ».
 */
export async function enregistrerReversementIRPP(
  tenantId: string,
  periode: string,
  input: { date_reversement: string; reference_paiement?: string; notes?: string },
): Promise<ReversementIRPPRecord> {
  const items = await load(tenantId);
  const now = nowIso();
  const existant = items.find((r) => r.periode === periode);
  if (existant) {
    const maj: ReversementIRPPRecord = {
      ...existant,
      date_reversement: input.date_reversement,
      reference_paiement: input.reference_paiement ?? existant.reference_paiement,
      notes: input.notes ?? existant.notes,
      maj_le: now,
    };
    await save(
      tenantId,
      items.map((r) => (r.id === existant.id ? maj : r)),
    );
    return maj;
  }
  const cree: ReversementIRPPRecord = {
    id: uuid(),
    tenant_id: tenantId,
    periode,
    date_reversement: input.date_reversement,
    reference_paiement: input.reference_paiement,
    notes: input.notes,
    cree_le: now,
    maj_le: now,
  };
  await save(tenantId, [...items, cree]);
  return cree;
}

/** Annule le marquage « reversé » (recalcule a_reverser / en_retard). */
export async function annulerReversementIRPP(tenantId: string, periode: string): Promise<void> {
  const items = await load(tenantId);
  const next = items.filter((r) => r.periode !== periode);
  await save(tenantId, next);
}
